import { SerializedStyles } from "@emotion/core";
import { EMAIL_REGEX } from "./regex";
import { AKAMAI_IMAGE_URL, IMAGE_KIT_URL } from "shared/constants";

export const validateEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(String(email).toLowerCase());
};
export const getGlobalThis = () => {
    if (typeof globalThis !== "undefined") return globalThis; // eslint-disable-line no-undef
    if (typeof self !== "undefined") return self;
    if (typeof window !== "undefined") return window;
    if (typeof global !== "undefined") return global;
    // @ts-ignore
    if (typeof this !== "undefined") return this;
    throw new Error("Unable to locate global `this`");
};

interface IImageOpts {
    height?: number;
    width?: number;
    pr?: boolean;
    q?: number;
    bl?: number;
    e?: string;
}

const getImageOptions = ({ height, width, pr, q, bl, e }: IImageOpts) => {
    const modifications = `${height ? `h-${height},` : ""}${width ? `w-${width},` : ""}${
        pr ? `pr-${pr},` : ""
    }${q ? `q-${q},` : ""}${bl ? `bl-${bl},` : ""}${e ? `e-${e},` : ""}`;
    return modifications.length > 0 ? `/tr:${modifications}` : "";
};

const akamaiRxp = new RegExp("^https://adn-static1.nykaa.com", "i");
export const nykaaRxp = new RegExp(
    "^https://(www|qa|preprod|qa-integration).nykaafashion.com",
    "i"
);
const imageHostRegExp = new RegExp(
    "^https://(images-static.nykaa.com|www.nykaafashion.com|qa.nykaafashion.com|preprod.nykaafashion.com|adn-static1.nykaa.com/nykdesignstudio-images|adn-static1.nykaa.com/uploads)/",
    "i"
);

const imageHostPDPVideo = new RegExp("^https://d2ec6rrjtftdyi.cloudfront.net/", "i");

const imageKitHostPDPVideo = `${IMAGE_KIT_URL}/fashion-nonprod-nf-catalog-pipeline`;

export const canAddAkamaiUrl = (url: string) =>
    akamaiRxp.test(url) === false &&
    nykaaRxp.test(url) === false &&
    imageHostPDPVideo.test(url) === false
        ? false
        : true;

export const generateSameHostUrl = (url: string) => {
    if (!nykaaRxp.test(url)) return url;

    return url.replace(nykaaRxp, "");
};

export const getOptimisedImageUrl = (
    imageId: string,
    options: IImageOpts = {},
    transformationType?: "append" | "overwrite"
) => {
    if (imageHostRegExp.test(imageId) === false) {
        return imageId;
    }

    if (imageId.includes("/tr:")) {
        let [before, after] = [...imageId.split("/tr:")],
            firstBackslashIndex = after.indexOf("/"),
            withoutTransformation = after.substring(firstBackslashIndex);

        if (transformationType === "append") return `${before}${getImageOptions(options)}${after}`;

        return `${before}${getImageOptions(options)}${withoutTransformation}`;
    } else {
        return imageId.replace(/\/([^/]*)$/, `${getImageOptions(options)}/$1`);
    }
};

export const getAkamaiUrl = (imageId: string, options: IImageOpts = {}) => {
    if (canAddAkamaiUrl(imageId) === false) {
        return imageId;
    }

    if (imageHostRegExp.test(imageId)) {
        const imgId = imageId.replace(imageHostRegExp, "").replace(/tr:[^/]+\//i, "");
        return `${AKAMAI_IMAGE_URL}${getImageOptions(options)}/${imgId}`;
    } else if (imageHostPDPVideo.test(imageId)) {
        const imgId = imageId.replace(imageHostPDPVideo, "").replace(/tr:[^/]+\//i, "");
        return `${imageKitHostPDPVideo}${getImageOptions(options)}/${imgId}`;
    } else {
        return `${AKAMAI_IMAGE_URL}${getImageOptions(options)}/${imageId}`;
    }
};

export const cssMerge = (
    baseCss: SerializedStyles,
    augmentCss?: SerializedStyles[] | SerializedStyles
) => {
    const cssProps = [baseCss];

    if (augmentCss) {
        Array.isArray(augmentCss)
            ? cssProps.push.apply(cssProps, augmentCss)
            : cssProps.push(augmentCss);
    }

    return cssProps;
};

export const setCookie = (
    cname: string,
    cvalue: string | number | boolean,
    days: number,
    domain?: string
) => {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = "expires=" + d.toUTCString();
    let Domain = "";
    if (domain && domain !== "") {
        Domain = ";Domain=" + domain;
    }
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/" + Domain;
};

const getCookieData = (name: string, decodedCookie: string) => {
    const ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) == " ") {
            c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
};

export const getCookie = (cname: string) => {
    const name = cname + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    return getCookieData(name, decodedCookie);
};

export const getCookieForServer = (cname: string, cookieData = "") => {
    const name = cname + "=";
    const decodedCookie = decodeURIComponent(cookieData);
    return getCookieData(name, decodedCookie);
};

export const inPriceFormat = (amount: string | number = "") => Number(amount).toLocaleString();

export const isScriptLoaded = (src: string) =>
    document.querySelector('script[src="' + src + '"]') ? true : false;
